import { h } from 'preact'
import { useSignal } from '@preact/signals'
import { useEffect } from 'preact/hooks'
import { chatApi } from '../chat-api.js'

/**
 * TaskCard — renders a task reference card inside a message bubble.
 *
 * Fetches task data from the API on mount and displays:
 *   - Reference code badge
 *   - Status badge (colored)
 *   - Task title
 *   - Building complex badges (first 2)
 *   - Responsible person name
 *   - Due date (red if overdue)
 *
 * On 404 or error: shows a "Task nem található" fallback card.
 *
 * Props: { taskId, store }
 */
export function TaskCard({ taskId, store }) {
  const task    = useSignal(null)   // null = loading, false = error/404
  const loading = useSignal(true)

  useEffect(() => {
    if (!taskId) {
      loading.value = false
      task.value    = false
      return
    }

    chatApi.tasks.show(taskId).then(async res => {
      loading.value = false
      if (res.ok) {
        const data = await res.json
        task.value = data.task ?? data
      } else {
        task.value = false
      }
    }).catch(() => {
      loading.value = false
      task.value    = false
    })
  }, [taskId]) // eslint-disable-line

  if (loading.value) {
    return (
      <div class="chat-task-card chat-task-card--loading">
        <div class="chat-task-card-header">
          <span class="chat-skeleton chat-skeleton--sm" style="width:60px" />
          <span class="chat-skeleton chat-skeleton--sm" style="width:40px" />
        </div>
        <div class="chat-skeleton" style="width:80%;height:14px;margin-top:6px" />
      </div>
    )
  }

  if (!task.value) {
    return (
      <div class="chat-task-card chat-task-card--unavailable">
        <div class="chat-task-card-header">
          <span class="chat-task-card-id">#{taskId}</span>
          <span class="chat-task-card-missing-badge">
            <i class="bi bi-exclamation-circle" />
          </span>
        </div>
        <div class="chat-task-card-missing-text">Task nem található</div>
      </div>
    )
  }

  const t            = task.value
  const url          = t.url || `/tasks/${t.id}`
  const refCode      = t.reference_code || t.ref_code || `#${t.id}`
  const statusLabel  = t.status_label   || t.status   || ''
  const statusColor  = t.status_color   || ''
  const bcNames      = t.building_complex_names ?? t.building_complexes ?? []
  const duedate      = t.duedate || t.due_date || null
  const isOverdue    = duedate && new Date(duedate) < new Date() && !['completed', 'cancelled'].includes(t.status)

  return (
    <a
      href={url}
      class="chat-task-card"
      target="_blank"
      rel="noopener noreferrer"
    >
      <div class="chat-task-card-header">
        <span class="chat-task-card-id">#{refCode.replace(/^#/, '')}</span>
        {statusLabel && (
          <span
            class="chat-task-card-status"
            style={statusColor
              ? `color:${statusColor};border-color:${statusColor};background-color:${statusColor}22`
              : undefined
            }
          >
            {statusLabel}
          </span>
        )}
      </div>

      <div class="chat-task-card-title">{t.name || t.title}</div>

      {bcNames.length > 0 && (
        <div class="chat-task-card-meta">
          {bcNames.slice(0, 2).map((name, i) => (
            <span key={i} class="chat-task-card-badge">
              {typeof name === 'string' ? name : name.name}
            </span>
          ))}
        </div>
      )}

      {t.responsible_name && (
        <div class="chat-task-card-responsible">
          <i class="bi bi-person-fill" />
          <span>{t.responsible_name}</span>
        </div>
      )}

      {duedate && (
        <div class={`chat-task-card-duedate${isOverdue ? ' chat-task-card-duedate--overdue' : ' text-muted'}`}>
          <i class="bi bi-calendar3" />
          <span>{formatDate(duedate)}</span>
        </div>
      )}
    </a>
  )
}

function formatDate(dateStr) {
  try {
    const d = new Date(dateStr)
    return d.toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return dateStr
  }
}
